import { useReducer, createContext, useMemo, useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import BottomNavigator from "../components/BottomNavigator";
import SideBar from '../components/SideBar';
import HeaderLarge from '../components/HeaderLarge';
import FooterComponent from '../components/FooterComponent';
import ScrollToTop from '../components/ScrollToTop';
import { getBranches, getCompanyInfo } from '../services/services';
import SpinnerLoader from '../components/SpinnerLoader';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

export const AuthContext = createContext();

const initialState = {
  openDrawer: false,
  isLoading: true,
  isSnackBarVisible: false,
  snackMessage: "",
  branchId: null,
  viewMode: sessionStorage.getItem("view_mode"),
  tableId: sessionStorage.getItem("table_id"),
  companyId: sessionStorage.getItem("companyId") ? Number(sessionStorage.getItem("companyId")) : null
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'TOGGLE_DRAWER':
      return { ...state, openDrawer: !state.openDrawer };
    case 'STORE_BRANCHID':
      return { ...state, branchId: action.branchId };
    case 'STORE_VIEW_MODE':
      return { ...state, viewMode: action.viewMode };
    case 'STORE_TABLE_ID':
      return { ...state, tableId: action.tableId };
    case 'STORE_COMPANY_ID':
      return { ...state, companyId: action.companyId };
    case 'TOGGLE_SNACKMESSAGE':
      return { ...state, isSnackBarVisible: action.isSnackVisible, snackMessage: action.message };
    case 'TOGGLE_LOADING':
      return { ...state, isLoading: action.isLoading };
    default:
      return state;
  }
};

export default function RootPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const mediaQueryList = window.matchMedia("(min-width: 992px)");
  const [isLargeDisplay, setIsLargeDisplay] = useState(mediaQueryList.matches);
  const [state, dispatch] = useReducer(reducer, initialState);

  const authContext = useMemo(() => ({
    toggleDrawer: () => dispatch({ type: 'TOGGLE_DRAWER' }),
    showSnackMessage: (message) => dispatch({ type: 'TOGGLE_SNACKMESSAGE', isSnackVisible: true, message }),
    getViewMode: () => state.viewMode,
    getBranchId: () => state.branchId,
    getCompanyId: () => state.companyId,
    getTableId: () => state.tableId,
  }), [state.viewMode, state.branchId, state.companyId, state.tableId]);

  useEffect(() => {
    const handleDeviceChange = (e) => setIsLargeDisplay(e.matches);
    mediaQueryList.addEventListener("change", handleDeviceChange);
    return () => mediaQueryList.removeEventListener("change", handleDeviceChange);
  }, [mediaQueryList]);

  const fetchBranchData = async () => {
    try {
      const branches = await getBranches(state.companyId);
      if (branches.status === 'success') {
        dispatch({ type: 'STORE_BRANCHID', branchId: branches.data[0].branchId });
        sessionStorage.setItem('storeId', branches.data[0].branchId);
        sessionStorage.setItem('loaded_once', "true");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await getCompanyInfo(state.companyId);
      if (response.status === 'success') {
        sessionStorage.setItem('companyInfo', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error("Error fetching company info:", error);
    }
  };

  const parsePathParams = () => {

    const params = location.pathname.split('/').filter(Boolean);
    const companyid = params[0];
    const isNumeric = (value) => !isNaN(value) && !isNaN(parseFloat(value));
    if (companyid && isNumeric(companyid)) {
      if (state.companyId !== companyid) {
        dispatch({ type: 'STORE_COMPANY_ID', companyId: companyid });
        sessionStorage.setItem("companyId", Number(companyid));
      }
      navigate("Home");
    }
    const encryptedString = params.slice(1).join('/');
    const isParamNum = (value) => !isNaN(value) && !isNaN(parseFloat(value));

    let viewMode;
    if (encryptedString && isParamNum(encryptedString) && isNumeric(companyid)) {
      viewMode = "table"
      dispatch({ type: 'STORE_TABLE_ID', tableId: encryptedString });
      dispatch({ type: 'STORE_VIEW_MODE', viewMode: viewMode });
      sessionStorage.setItem("table_id", Number(encryptedString));
      if (viewMode) dispatch({ type: 'STORE_VIEW_MODE', viewMode: viewMode });

    } else {
      if (encryptedString === "ycuiUwYgxbV+hlPL/AhFgg==") viewMode = "menu";
      else if (encryptedString === "HFCMUYNZqhe987T7QbYLSA==") viewMode = "takeaway";
      else if (encryptedString === "OP+SQ+MZXgDKNYXshoMa0w==") viewMode = "homedelivery";
      if (viewMode) dispatch({ type: 'STORE_VIEW_MODE', viewMode: viewMode });
    }
    if (viewMode) {
      sessionStorage.setItem("view_mode", viewMode)
    }
  };

  useEffect(() => {
    parsePathParams();
  }, [location.pathname]);

  const fetchIds = async () => {
    dispatch({ type: 'TOGGLE_LOADING', isLoading: true });
    try {
      await fetchBranchData();
      await fetchCompanyInfo();
    } catch (error) {

    }
    dispatch({ type: 'TOGGLE_LOADING', isLoading: false });
  }

  const closeSnack = () => {
    dispatch({ type: 'TOGGLE_SNACKMESSAGE', isSnackVisible: false, message:"" })
  }

  const action = (
    <>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={closeSnack}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  useEffect(() => {
    if (state.companyId) {
      fetchIds()
    }
  }, [state.companyId])

  return (
    (!state.isLoading) ? (
      <AuthContext.Provider value={authContext}>
        <ScrollToTop />
        {!isLargeDisplay && <SideBar drawerState={state.openDrawer} />}
        {isLargeDisplay && <HeaderLarge />}
        <Outlet />
        {!isLargeDisplay && <BottomNavigator />}
        {isLargeDisplay && <FooterComponent />}
        <Snackbar
          open={state.isSnackBarVisible}
          autoHideDuration={2000}
          onClose={closeSnack}
          message={state.snackMessage}
          action={action}
        />
      </AuthContext.Provider>
    ) : (
      <div className='SpinnerContainer'>
        <SpinnerLoader />
      </div>
    )
  );
}
